/**
 * Benefits & Coupon Console — admin-side domain helpers.
 *
 * This module is presentation and administration only. It reads canonical
 * prices from the sealed Pricing Engine and never computes a charge: the live
 * amount a buyer pays is still decided by the engine and the gateway edge
 * functions. Everything here shapes forms, previews, validation, exports and
 * audit context for the Admin Hub.
 */

import { REGIONS, type RegionCode } from "@/pricing";
import type { Database } from "@/integrations/supabase/types";

/* ------------------------------------------------------------------ types */

export type BenefitTypeDb = Database["public"]["Enums"]["benefit_type"];
export type BenefitStatusDb = Database["public"]["Enums"]["benefit_status"];
export type DiscountTypeDb = "percent" | "flat" | "full";
export type PricingMode = "percentage" | "fixed_amount" | "final_price";

export interface CouponRow {
  id: string;
  code: string;
  label: string;
  description: string | null;
  partner_org: string | null;
  benefit_type: BenefitTypeDb;
  status: BenefitStatusDb;
  discount_type: DiscountTypeDb;
  discount_value: number;
  pricing_mode: PricingMode;
  final_price: number | null;
  currency: string;
  regions: string[];
  timezone: string;
  max_redemptions: number | null;
  redemption_count: number;
  max_uses_per_user: number | null;
  max_uses_per_email: number | null;
  max_uses_per_institution: number | null;
  max_uses_per_partner: number | null;
  min_purchase_amount: number | null;
  eligible_membership: string;
  eligible_institutions: string[];
  eligible_partners: string[];
  eligible_countries: string[];
  internal_only: boolean;
  requires_approval: boolean;
  stackable: boolean;
  priority: number;
  tags: string[];
  batch_id: string | null;
  valid_from: string | null;
  valid_until: string | null;
  active: boolean;
  notes: string | null;
  archived_at: string | null;
  deleted_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/* ---------------------------------------------------------------- options */

/** Benefit types offered in the console, mapped to the database enum. */
export const BENEFIT_TYPE_OPTIONS: Array<{
  value: BenefitTypeDb;
  label: string;
  /** Set when the label is a presentation name for a shared database type. */
  tag?: string;
  hint?: string;
}> = [
  { value: "PUBLIC_CAMPAIGN", label: "Campaign", hint: "Time-boxed public offer" },
  { value: "SCHOLARSHIP", label: "Scholarship", hint: "Needs-based, approval-gated" },
  { value: "PARTNER", label: "Partner", hint: "Priced by partner agreement" },
  { value: "INSTITUTION", label: "Institution", hint: "School or college licence" },
  { value: "REFERRAL", label: "Referral", hint: "Earned by referring someone" },
  { value: "AMBASSADOR", label: "Ambassador", hint: "Community advocate reward" },
  { value: "STAFF", label: "Staff", hint: "Internal team" },
  { value: "ADMIN", label: "Admin", hint: "Support and goodwill" },
  {
    value: "PRIVATE_INVITATION",
    label: "Friends & Family",
    tag: "friends-family",
    hint: "Unique private invitation codes",
  },
];

export const benefitTypeLabel = (t: BenefitTypeDb, tags: string[] = []) => {
  if (t === "PRIVATE_INVITATION" && tags.includes("friends-family")) return "Friends & Family";
  return BENEFIT_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t;
};

export const STATUS_OPTIONS: Array<{ value: BenefitStatusDb; label: string; hint: string }> = [
  { value: "draft", label: "Draft", hint: "Never offered — safe to keep editing" },
  { value: "scheduled", label: "Scheduled", hint: "Starts on its start date" },
  { value: "active", label: "Active", hint: "Live and redeemable now" },
  { value: "paused", label: "Paused", hint: "Temporarily not redeemable" },
  { value: "expired", label: "Expired", hint: "Past its end date" },
  { value: "archived", label: "Archived", hint: "Kept for the record only" },
];

/** Only 'active' is redeemable. Everything else must be inactive. */
export const isRedeemableStatus = (s: BenefitStatusDb) => s === "active";

export const PRICING_MODE_OPTIONS: Array<{ value: PricingMode; label: string }> = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed_amount", label: "Fixed amount" },
  { value: "final_price", label: "Final price override" },
];

export const MEMBERSHIP_OPTIONS = [
  { value: "everyone", label: "Everyone" },
  { value: "students", label: "Students" },
  { value: "parents", label: "Parents" },
  { value: "teachers", label: "Teachers" },
  { value: "institutions", label: "Institutions" },
  { value: "partners", label: "Partners" },
  { value: "admins", label: "Admins" },
];

/**
 * Regions a coupon can be tagged for. IN and US are live pricing regions in the
 * engine; the rest are declared so a launch is configuration, not code.
 */
export const REGION_OPTIONS: Array<{
  value: string;
  label: string;
  currency: string;
  live: boolean;
}> = [
  { value: "IN", label: "India", currency: "INR", live: true },
  { value: "US", label: "USA", currency: "USD", live: true },
  { value: "CA", label: "Canada", currency: "CAD", live: false },
  { value: "GB", label: "UK", currency: "GBP", live: false },
  { value: "AU", label: "Australia", currency: "AUD", live: false },
  { value: "ME", label: "Middle East", currency: "AED", live: false },
  { value: "EU", label: "Europe", currency: "EUR", live: false },
  { value: "AF", label: "Africa", currency: "ZAR", live: false },
  { value: "GLOBAL", label: "Global", currency: "USD", live: false },
];

export const CURRENCY_OPTIONS = ["INR", "USD", "CAD", "GBP", "AUD", "AED", "EUR"] as const;

export const CODE_FORMAT_OPTIONS = [
  { value: "random", label: "Random" },
  { value: "prefix", label: "Prefix + Random" },
] as const;

export const BATCH_SIZE_OPTIONS = [10, 50, 100, 500, 1000] as const;

export const TAG_SUGGESTIONS = [
  "Product Hunt",
  "Launch",
  "Investor",
  "School",
  "Partner",
  "friends-family",
];

export const TIMEZONE_OPTIONS = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Asia/Dubai",
  "Australia/Sydney",
];

/* -------------------------------------------------------- canonical price */

/**
 * Canonical MRP for a region, straight from the sealed pricing config.
 * Regions the engine does not price yet fall back to their declared list price
 * or the international price.
 */
export function canonicalMrp(region: string): { amount: number; currency: string } {
  const engineRegion = REGIONS[region as RegionCode];
  if (engineRegion) {
    return { amount: engineRegion.listPrice, currency: engineRegion.currency };
  }
  const declared = REGION_OPTIONS.find((r) => r.value === region);
  return {
    amount: REGIONS.US.listPrice,
    currency: declared?.currency ?? REGIONS.US.currency,
  };
}

export function currencyForRegions(regions: string[]): string {
  if (regions.includes("IN")) return "INR";
  const first = regions.find((r) => r !== "GLOBAL");
  return REGION_OPTIONS.find((r) => r.value === first)?.currency ?? "USD";
}

export function formatMoney(amount: number | null | undefined, currency = "INR"): string {
  const value = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${currency} ${value}`;
  }
}

/* ------------------------------------------------------------ live preview */

export interface PreviewInput {
  pricingMode: PricingMode;
  percentage: number;
  fixedAmount: number;
  finalPrice: number;
  mrp: number;
  currency: string;
}

export interface PreviewResult {
  mrp: number;
  currency: string;
  /** Percentage off, rounded to one decimal for display. */
  percentOff: number;
  savings: number;
  finalPrice: number;
  /** Discount type + value as they will be stored for the engine. */
  discountType: DiscountTypeDb;
  discountValue: number;
}

/**
 * What the buyer would see. Mirrors the engine's arithmetic for display only —
 * the stored `discount_type` / `discount_value` are what the engine actually
 * uses at checkout, and they are derived here so the two can never disagree.
 */
export function previewPrice(input: PreviewInput): PreviewResult {
  const mrp = Math.max(Number(input.mrp) || 0, 0);
  let savings = 0;
  let discountType: DiscountTypeDb = "percent";
  let discountValue = 0;

  if (input.pricingMode === "percentage") {
    const pct = clamp(Number(input.percentage) || 0, 0, 100);
    savings = round2((mrp * pct) / 100);
    discountType = pct >= 100 ? "full" : "percent";
    discountValue = pct;
  } else if (input.pricingMode === "fixed_amount") {
    const flat = clamp(Number(input.fixedAmount) || 0, 0, mrp);
    savings = round2(flat);
    discountType = flat >= mrp ? "full" : "flat";
    discountValue = flat;
  } else {
    const final = clamp(Number(input.finalPrice) || 0, 0, mrp);
    savings = round2(mrp - final);
    discountType = final <= 0 ? "full" : "flat";
    discountValue = savings;
  }

  const finalPrice = round2(Math.max(mrp - savings, 0));
  return {
    mrp,
    currency: input.currency,
    percentOff: mrp > 0 ? round1((savings / mrp) * 100) : 0,
    savings,
    finalPrice,
    discountType,
    discountValue,
  };
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);
const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

/* ------------------------------------------------------------- validation */

export interface CouponFormValues {
  label: string;
  code: string;
  description: string;
  benefit_type: BenefitTypeDb;
  pricing_mode: PricingMode;
  percentage: string;
  fixed_amount: string;
  final_price: string;
  mrp: string;
  regions: string[];
  currency: string;
  timezone: string;
  valid_from: string;
  valid_until: string;
  status: BenefitStatusDb;
  max_redemptions: string;
  max_uses_per_user: string;
  max_uses_per_email: string;
  max_uses_per_institution: string;
  max_uses_per_partner: string;
  min_purchase_amount: string;
  eligible_membership: string;
  eligible_institutions: string[];
  eligible_partners: string[];
  eligible_countries: string[];
  stackable: boolean;
  priority: string;
  internal_only: boolean;
  requires_approval: boolean;
  auto_generate: boolean;
  batch_size: number;
  code_format: "random" | "prefix";
  code_prefix: string;
  notes: string;
  tags: string[];
  partner_org: string;
}

export const emptyCouponForm = (): CouponFormValues => ({
  label: "",
  code: "",
  description: "",
  benefit_type: "PUBLIC_CAMPAIGN",
  pricing_mode: "percentage",
  percentage: "20",
  fixed_amount: "",
  final_price: "",
  mrp: String(REGIONS.IN.listPrice),
  regions: ["IN"],
  currency: "INR",
  timezone: "Asia/Kolkata",
  valid_from: "",
  valid_until: "",
  status: "draft",
  max_redemptions: "",
  max_uses_per_user: "1",
  max_uses_per_email: "1",
  max_uses_per_institution: "",
  max_uses_per_partner: "",
  min_purchase_amount: "",
  eligible_membership: "everyone",
  eligible_institutions: [],
  eligible_partners: [],
  eligible_countries: [],
  stackable: false,
  priority: "100",
  internal_only: false,
  requires_approval: false,
  auto_generate: false,
  batch_size: 10,
  code_format: "prefix",
  code_prefix: "FNF",
  notes: "",
  tags: [],
  partner_org: "",
});

export type FieldErrors = Partial<Record<keyof CouponFormValues | "form", string>>;

const CODE_RE = /^[A-Z0-9][A-Z0-9_-]{2,49}$/;

export function validateCouponForm(
  values: CouponFormValues,
  opts: { existingCodes?: string[]; isSuperAdmin?: boolean } = {},
): FieldErrors {
  const errors: FieldErrors = {};
  const existing = (opts.existingCodes ?? []).map((c) => c.toUpperCase());

  if (!values.label.trim()) errors.label = "Give the campaign a name.";
  if (!values.auto_generate) {
    const code = values.code.trim().toUpperCase();
    if (!code) errors.code = "A coupon code is required.";
    else if (!CODE_RE.test(code))
      errors.code = "Use 3–50 characters: A–Z, 0–9, hyphen or underscore.";
    else if (existing.includes(code)) errors.code = "That code already exists.";
  } else if (values.code_format === "prefix" && !values.code_prefix.trim()) {
    errors.code_prefix = "A prefix is required for prefixed codes.";
  }

  if (values.regions.length === 0) errors.regions = "Pick at least one region.";
  if (!CURRENCY_OPTIONS.includes(values.currency as (typeof CURRENCY_OPTIONS)[number]))
    errors.currency = "Unsupported currency.";

  const mrp = Number(values.mrp);
  if (!Number.isFinite(mrp) || mrp <= 0) errors.mrp = "The MRP must be greater than zero.";

  if (values.pricing_mode === "percentage") {
    const pct = Number(values.percentage);
    if (!Number.isFinite(pct)) errors.percentage = "Enter a percentage.";
    else if (pct < 0) errors.percentage = "A discount cannot be negative.";
    else if (pct > 100) errors.percentage = "A discount cannot exceed 100%.";
  } else if (values.pricing_mode === "fixed_amount") {
    const flat = Number(values.fixed_amount);
    if (!Number.isFinite(flat) || values.fixed_amount === "")
      errors.fixed_amount = "Enter an amount.";
    else if (flat < 0) errors.fixed_amount = "A discount cannot be negative.";
    else if (flat > mrp) errors.fixed_amount = "The discount cannot exceed the MRP.";
  } else {
    const final = Number(values.final_price);
    if (!Number.isFinite(final) || values.final_price === "")
      errors.final_price = "Enter the selling price.";
    else if (final < 0) errors.final_price = "A price cannot be negative.";
    else if (final > mrp) errors.final_price = "The selling price cannot exceed the MRP.";
  }

  if (values.valid_from && values.valid_until) {
    if (new Date(values.valid_until).getTime() <= new Date(values.valid_from).getTime())
      errors.valid_until = "The end date must be after the start date.";
  }
  if (values.status === "scheduled" && !values.valid_from)
    errors.valid_from = "A scheduled campaign needs a start date.";

  for (const key of [
    "max_redemptions",
    "max_uses_per_user",
    "max_uses_per_email",
    "max_uses_per_institution",
    "max_uses_per_partner",
    "min_purchase_amount",
    "priority",
  ] as const) {
    const raw = values[key];
    if (raw === "" || raw === null || raw === undefined) continue;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) errors[key] = "Enter a number of zero or more.";
  }

  const preview = previewPrice(previewInputFrom(values));
  if (preview.finalPrice < 0) errors.form = "That combination produces a negative price.";
  if (preview.discountType === "full" && !opts.isSuperAdmin)
    errors.form = "A 100% free coupon needs a super admin.";

  return errors;
}

export const previewInputFrom = (values: CouponFormValues): PreviewInput => ({
  pricingMode: values.pricing_mode,
  percentage: Number(values.percentage) || 0,
  fixedAmount: Number(values.fixed_amount) || 0,
  finalPrice: Number(values.final_price) || 0,
  mrp: Number(values.mrp) || 0,
  currency: values.currency,
});

/* ------------------------------------------------------- code generation */

const CODE_ALPHABET = "23456789BCDFGHJKLMNPQRSTVWXZ";

function randomChunk(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join("");
}

/** Generate unique codes, avoiding anything already taken. */
export function generateCodes(opts: {
  count: number;
  format: "random" | "prefix";
  prefix?: string;
  length?: number;
  taken?: string[];
}): string[] {
  const taken = new Set((opts.taken ?? []).map((c) => c.toUpperCase()));
  const out: string[] = [];
  const length = opts.length ?? 6;
  let guard = 0;
  while (out.length < opts.count && guard < opts.count * 50) {
    guard += 1;
    const body = randomChunk(length);
    const code =
      opts.format === "prefix" && opts.prefix?.trim()
        ? `${opts.prefix.trim().toUpperCase().replace(/[^A-Z0-9]/g, "")}-${body}`
        : body;
    if (taken.has(code)) continue;
    taken.add(code);
    out.push(code);
  }
  return out;
}

/* ------------------------------------------------------- row <-> payload */

export function formFromRow(row: CouponRow): CouponFormValues {
  const base = emptyCouponForm();
  const mrp = row.final_price != null && row.discount_type === "flat"
    ? Number(row.final_price) + Number(row.discount_value)
    : canonicalMrp(row.regions[0] ?? "IN").amount;
  return {
    ...base,
    label: row.label,
    code: row.code,
    description: row.description ?? "",
    benefit_type: row.benefit_type,
    pricing_mode: row.pricing_mode,
    percentage: row.discount_type === "percent" ? String(row.discount_value) : base.percentage,
    fixed_amount: row.discount_type === "flat" ? String(row.discount_value) : "",
    final_price: row.final_price != null ? String(row.final_price) : "",
    mrp: String(mrp),
    regions: row.regions?.length ? row.regions : ["IN"],
    currency: row.currency,
    timezone: row.timezone,
    valid_from: toLocalInput(row.valid_from),
    valid_until: toLocalInput(row.valid_until),
    status: row.status,
    max_redemptions: numToInput(row.max_redemptions),
    max_uses_per_user: numToInput(row.max_uses_per_user),
    max_uses_per_email: numToInput(row.max_uses_per_email),
    max_uses_per_institution: numToInput(row.max_uses_per_institution),
    max_uses_per_partner: numToInput(row.max_uses_per_partner),
    min_purchase_amount: numToInput(row.min_purchase_amount),
    eligible_membership: row.eligible_membership,
    eligible_institutions: row.eligible_institutions ?? [],
    eligible_partners: row.eligible_partners ?? [],
    eligible_countries: row.eligible_countries ?? [],
    stackable: row.stackable,
    priority: String(row.priority),
    internal_only: row.internal_only,
    requires_approval: row.requires_approval,
    notes: row.notes ?? "",
    tags: row.tags ?? [],
    partner_org: row.partner_org ?? "",
  };
}

const numToInput = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
const toLocalInput = (iso: string | null) => (iso ? iso.slice(0, 16) : "");
const inputToIso = (local: string) => (local ? new Date(local).toISOString() : null);
const orNull = (raw: string) => (raw === "" ? null : Number(raw));

/**
 * The row written to the database. `discount_type` / `discount_value` /
 * `active` are derived here so the engine reads exactly what the preview shows.
 */
export function payloadFromForm(values: CouponFormValues, code?: string) {
  const preview = previewPrice(previewInputFrom(values));
  return {
    code: (code ?? values.code).trim().toUpperCase(),
    label: values.label.trim(),
    description: values.description.trim() || null,
    benefit_type: values.benefit_type,
    status: values.status,
    active: isRedeemableStatus(values.status),
    discount_type: preview.discountType,
    discount_value: preview.discountValue,
    pricing_mode: values.pricing_mode,
    final_price: preview.finalPrice,
    currency: values.currency,
    regions: values.regions,
    timezone: values.timezone,
    max_redemptions: orNull(values.max_redemptions),
    max_uses_per_user: orNull(values.max_uses_per_user),
    max_uses_per_email: orNull(values.max_uses_per_email),
    max_uses_per_institution: orNull(values.max_uses_per_institution),
    max_uses_per_partner: orNull(values.max_uses_per_partner),
    min_purchase_amount: orNull(values.min_purchase_amount),
    eligible_membership: values.eligible_membership,
    eligible_institutions: values.eligible_institutions,
    eligible_partners: values.eligible_partners,
    eligible_countries: values.eligible_countries,
    stackable: values.stackable,
    priority: Number(values.priority) || 100,
    internal_only: values.internal_only,
    requires_approval: values.requires_approval,
    tags: values.tags,
    valid_from: inputToIso(values.valid_from),
    valid_until: inputToIso(values.valid_until),
    notes: values.notes.trim() || null,
    partner_org: values.partner_org.trim() || null,
  };
}

/* ------------------------------------------------------------ audit context */

/**
 * A stable, non-reversible fingerprint of the admin's network, for the audit
 * trail. We never store or transmit a raw IP from the browser.
 */
export async function auditIpHash(): Promise<string | null> {
  try {
    const seed = [
      navigator.userAgent,
      navigator.language,
      String(new Date().getTimezoneOffset()),
      screen.width + "x" + screen.height,
    ].join("|");
    const bytes = new TextEncoder().encode(seed);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);
  } catch {
    return null;
  }
}

/* ---------------------------------------------------------------- exports */

export const COUPON_EXPORT_COLUMNS = [
  "code",
  "label",
  "description",
  "benefit_type",
  "status",
  "pricing_mode",
  "discount_type",
  "discount_value",
  "final_price",
  "currency",
  "regions",
  "valid_from",
  "valid_until",
  "max_redemptions",
  "redemption_count",
  "max_uses_per_user",
  "max_uses_per_email",
  "eligible_membership",
  "stackable",
  "priority",
  "internal_only",
  "requires_approval",
  "tags",
  "notes",
  "created_at",
] as const;

export function couponsToRecords(rows: CouponRow[]): Array<Record<string, string | number>> {
  return rows.map((r) => {
    const out: Record<string, string | number> = {};
    for (const key of COUPON_EXPORT_COLUMNS) {
      const value = (r as unknown as Record<string, unknown>)[key];
      out[key] = Array.isArray(value)
        ? value.join("|")
        : value === null || value === undefined
          ? ""
          : typeof value === "boolean"
            ? value
              ? "yes"
              : "no"
            : (value as string | number);
    }
    return out;
  });
}

export function toCsv(records: Array<Record<string, unknown>>): string {
  if (records.length === 0) return "";
  const headers = Object.keys(records[0]);
  const cell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [
    headers.join(","),
    ...records.map((r) => headers.map((h) => cell(r[h])).join(",")),
  ].join("\r\n");
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ----------------------------------------------------------------- import */

export interface ImportRow {
  values: CouponFormValues;
  errors: FieldErrors;
  raw: Record<string, string>;
}

/** Parse a CSV (header row required) into validated coupon drafts. */
export function parseCouponCsv(
  text: string,
  opts: { existingCodes?: string[]; isSuperAdmin?: boolean } = {},
): { rows: ImportRow[]; fatal?: string } {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return { rows: [], fatal: "The file needs a header row and at least one coupon." };

  const headers = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  if (!headers.includes("code") || !headers.includes("label")) {
    return { rows: [], fatal: "The header row must include at least 'code' and 'label'." };
  }

  const seen = new Set((opts.existingCodes ?? []).map((c) => c.toUpperCase()));
  const rows: ImportRow[] = [];

  for (const line of lines.slice(1)) {
    const cells = splitCsvLine(line);
    const raw: Record<string, string> = {};
    headers.forEach((h, i) => {
      raw[h] = (cells[i] ?? "").trim();
    });

    const regions = (raw.regions || "IN").split(/[|,]/).map((r) => r.trim().toUpperCase()).filter(Boolean);
    const currency = raw.currency?.toUpperCase() || currencyForRegions(regions);
    const values: CouponFormValues = {
      ...emptyCouponForm(),
      label: raw.label ?? "",
      code: (raw.code ?? "").toUpperCase(),
      description: raw.description ?? "",
      benefit_type: (BENEFIT_TYPE_OPTIONS.find(
        (o) => o.value === raw.benefit_type?.toUpperCase() || o.label.toLowerCase() === raw.benefit_type?.toLowerCase(),
      )?.value ?? "PUBLIC_CAMPAIGN") as BenefitTypeDb,
      pricing_mode: (["percentage", "fixed_amount", "final_price"].includes(raw.pricing_mode)
        ? raw.pricing_mode
        : raw.final_price
          ? "final_price"
          : raw.discount_type === "flat"
            ? "fixed_amount"
            : "percentage") as PricingMode,
      percentage: raw.percentage || (raw.discount_type === "percent" ? raw.discount_value : "") || "0",
      fixed_amount: raw.fixed_amount || (raw.discount_type === "flat" ? raw.discount_value : "") || "",
      final_price: raw.final_price || "",
      mrp: raw.mrp || String(canonicalMrp(regions[0] ?? "IN").amount),
      regions,
      currency,
      status: (STATUS_OPTIONS.find((s) => s.value === raw.status)?.value ?? "draft") as BenefitStatusDb,
      valid_from: raw.valid_from ? raw.valid_from.slice(0, 16) : "",
      valid_until: raw.valid_until ? raw.valid_until.slice(0, 16) : "",
      max_redemptions: raw.max_redemptions ?? "",
      max_uses_per_user: raw.max_uses_per_user || "1",
      max_uses_per_email: raw.max_uses_per_email || "1",
      eligible_membership:
        MEMBERSHIP_OPTIONS.find((m) => m.value === raw.eligible_membership?.toLowerCase())?.value ?? "everyone",
      stackable: /^(yes|true|1)$/i.test(raw.stackable ?? ""),
      priority: raw.priority || "100",
      internal_only: /^(yes|true|1)$/i.test(raw.internal_only ?? ""),
      requires_approval: /^(yes|true|1)$/i.test(raw.requires_approval ?? ""),
      notes: raw.notes ?? "",
      tags: (raw.tags ?? "").split(/[|,]/).map((t) => t.trim()).filter(Boolean),
    };

    const errors = validateCouponForm(values, {
      existingCodes: Array.from(seen),
      isSuperAdmin: opts.isSuperAdmin,
    });
    if (!errors.code) seen.add(values.code);
    rows.push({ values, errors, raw });
  }

  return { rows };
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

export const CSV_TEMPLATE = [
  "code,label,description,benefit_type,pricing_mode,percentage,fixed_amount,final_price,currency,regions,status,valid_from,valid_until,max_redemptions,max_uses_per_user,eligible_membership,priority,tags,notes",
  'LAUNCH20,Launch offer,20% off for launch week,PUBLIC_CAMPAIGN,percentage,20,,,INR,IN,draft,,,500,1,everyone,100,Launch,Created from CSV',
].join("\r\n");
